import { flow, getEnv, types } from "mobx-state-tree";
import { DataGridStorageModel } from "../../components/datatable/DataGridStorage";
import { Column, ColumnType } from "../../components/datatable/interfaces";
import { HeaderItemResponse, InspectionDataGridResponseWithDynamicProperties, InspectionRowResponse } from "../../api-models/response/DataGridResponse";
import { sortColumns } from "../../components/datatable/helpers/sortExtensions";
import { endpoints } from "../api/ApiEndpoints";
import { Inspection } from "./Inspection";

const esicTableColumns: Column[] = [
    {
        key: "SewerObject",
        name: "general.sewer-objects.list-header.sewer-object-id",
        type: ColumnType.String,
        filterable: true,
        sortable: true,
        containsLink: true,
    },
    {
        key: "Inspection",
        name: "general.inspection",
        type: ColumnType.String,
        containsLink: true,
    },
    {
        key: "IsRelevant",
        name: "general.inspections.list-header.relevance",
        type: ColumnType.Boolean,
        filterable: true,
        sortable: true
    },
    {
        key: "OperatorScore",
        name: "general.inspections.comparison.f1-operator",
        type: ColumnType.Number,
        filterable: true,
        sortable: true,
        containsLink: true,
        showsProgress: true
    },
    {
        key: "AIScore",
        name: "general.inspections.comparison.f1-ai",
        type: ColumnType.Number,
        filterable: true,
        sortable: true,
        containsLink: true,
        showsProgress: true
    },
    {
        key: "TruePositives",
        name: "general.inspections.comparison.true-positives",
        type: ColumnType.Number,
        filterable: true,
        sortable: true,
        containsLink: true,
    },
    {
        key: "FalseNegatives",
        name: "general.inspections.comparison.false-negatives",
        type: ColumnType.Number,
        filterable: true,
        sortable: true,
        containsLink: true,
    },
    {
        key: "FalsePositives",
        name: "general.inspections.comparison.false-positives",
        type: ColumnType.Number,
        filterable: true,
        sortable: true,
        containsLink: true,
    },
    {
        key: "BM426",
        name: "general.inspections.comparison.length",
        type: ColumnType.Number,
        filterable: true,
        sortable: true,
        containsLink: true,
    },
    {
        key: "BM502",
        name: "general.inspections.comparison.node-inflow",
        type: ColumnType.Number,
        filterable: true,
        sortable: true,
        containsLink: true,
    },
    {
        key: "BM504",
        name: "general.inspections.comparison.node-sequence",
        type: ColumnType.Number,
        filterable: true,
        sortable: true,
        containsLink: true,
    },
    {
        key: "BM408",
        name: "general.inspections.comparison.direction",
        type: ColumnType.String,
        filterable: true,
        sortable: true,
        containsLink: true,
    },
    {
        key: "BM999",
        name: "general.inspections.comparison.comment",
        type: ColumnType.String,
        filterable: true,
        sortable: true,
        containsLink: true,
    },
    {
        key: "GatheringDate",
        name: "general.inspections.files.list-header.gatheringDate",
        type: ColumnType.DateTime,
        filterable: true,
        sortable: true
    },
    {
        key: "Modified",
        name: "general.last-edit",
        type: ColumnType.DateTime,
        filterable: true,
        sortable: true
    },
]

export const ProjectInspectionsStorageModel = types
    .compose(
        types.model({
            projectId: types.number,
            isDeletingInspections: false,
        }),
        DataGridStorageModel
    )
    .named("ProjectInspectionsStorageModel")
    .actions((self) => {
        const getHeaders = (response: InspectionDataGridResponseWithDynamicProperties) => {
            const inspectionHeaders = response.header as HeaderItemResponse[];
            if (!inspectionHeaders.length) return esicTableColumns;
      
            inspectionHeaders.sort(sortColumns);
      
            return esicTableColumns;
        };
        return {
            mapItem(item: InspectionRowResponse) {
                return Inspection._constructorFromRowItem(item, `/projects/${self.projectId}`);
              },
              mapResponse(response: InspectionDataGridResponseWithDynamicProperties) {
                self.data = {
                  totalCount: response.totalCount,
                  items: response.rows.map(self.mapItem),
                };
                self.gridColumns = getHeaders(response);
            },
            setIsDeletingInspections(value: boolean) {
                self.isDeletingInspections = value;
            },
            deleteInspections: flow(function* (inspectionIds: number[]) {
                const endpoint = endpoints.get("inspections");
                yield getEnv(self).api.delete(endpoint, inspectionIds);
                self.deselectAll();
                yield self.load();
            }),
            updateComparison: flow(function* () {
                var endpoint = endpoints.get("inspections_update_comparison");
                endpoint.routeParams = [self.projectId];
                yield getEnv(self).api.get(endpoint);
                endpoint = endpoints.get("inspections_update_comparison_async");
                endpoint.routeParams = [self.projectId];
                yield getEnv(self).api.getAsync(endpoint);
            }),
        }
    })
    .views((self) => ({
        get isLoading() {
            return (!self.data && !self.error);
        },
        get endpoint() {
            const endpoint = endpoints.get("project_inspections_all");
            endpoint.routeParams = [self.projectId];
            endpoint.queryParams = `projectId=${self.projectId}&${self.getDataQuery()}`;
            return endpoint;
          },
          get canLoadItems() {
            return !!self.projectId;
          },
          get endpointOnlyIds() {
            const endpoint = endpoints.get("project_inspection_ids_all");
            endpoint.routeParams = [self.projectId];
            return endpoint;
          },
    }));